import {
  Dispatch,
  FunctionComponent,
  ReactNode,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Placement,
  UseFloatingOptions,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import { ExpandMore, Search } from 'icons';
import { FormattedMessage } from 'localization';
import { twMerge } from 'tailwind-merge';
import { Copy, TextInput } from 'ui/atoms';

import { Tooltip } from '../../../molecules';
import useClickAway from '../../hooks/useClickAway';
import { DefaultDropdownValue } from './DefaultDropdownValue';
import { DefaultOption } from './DefaultOption';
import { DropdownPopup, type TPopupPositions } from './DropdownPopup';

export interface ValueComponentProps<Data = any> {
  placeholder?: IDropdownProps['placeholder'];
  value?: Data;
  open: boolean;
  multiple?: boolean;
  isError?: boolean;
  disabled?: boolean;
  className?: string;
  icon?: ReactNode;
  wrapperClassName?: string;
}

export interface OptionComponentProps {
  option?: TOption;
  selected?: boolean;
  preselected?: boolean;
}

const defaultFloatingOptions = (position: TPopupPositions): UseFloatingOptions => ({
  placement: position as Placement,
  middleware: [
    offset(4),
    flip(),
    shift(),
    size({
      apply({ availableWidth, availableHeight, elements }) {
        Object.assign(elements.floating.style, {
          width: '100%',
          maxWidth: `${availableWidth}px`,
          maxHeight: `${availableHeight - 24}px`,
        });
      },
    }),
  ],
  whileElementsMounted: autoUpdate,
});

export function Dropdown({
  open: customOpen,
  setOpen: customSetOpen,
  placeholder,
  value,
  withSearch,
  toolTipTitle,
  onChange = () => null,
  options: initialOptions = [],
  className,
  disabled = false,
  classNamePopup,
  classNameValue,
  wrapperValueClassName,
  multiple = false,
  Value = DefaultDropdownValue,
  Option = DefaultOption,
  position = 'bottom-start',
  floatingOptions = defaultFloatingOptions(position),
  isError,
  icon,
}: IDropdownProps) {
  const [_open, _setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<TOption[]>(initialOptions);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const open = customOpen ?? _open;
  const setOpen = customSetOpen || _setOpen;
  const ref = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useClickAway(ref, () => setOpen(false));

  const selectedOptions = useMemo(() => {
    if (multiple && Array.isArray(value)) {
      return value.map((v) => initialOptions.find((o) => o.value === v) ?? { label: v, value: v });
    }
    const found = initialOptions.find((o) => o.value === value);
    return found ?? (value == null ? undefined : { label: value, value });
  }, [multiple, value, initialOptions]);

  const selectedLabel = useMemo(() => {
    if (multiple && Array.isArray(selectedOptions)) {
      return selectedOptions.map((o) => normalizeLabel(o.label)).join(', ');
    }
    return selectedOptions ? normalizeLabel((selectedOptions as TOption).label) : '';
  }, [multiple, selectedOptions]);

  const valueObj = useMemo(() => {
    if (multiple && Array.isArray(value)) {
      return value.map((val) => {
        const option = options.find((option) => option.value === val);

        if (option) {
          return option;
        }

        return { label: val, value: val };
      });
    }
    const option = options.find((option) => option.value === value);
    return option || { label: '', value: '' };
  }, [multiple, value, options]);

  const searchButtonValue = useMemo(() => {
    if (multiple && Array.isArray(valueObj)) {
      return valueObj.map((option) => option.label).join(', ');
    }
    return (valueObj as TOption)?.label || '';
  }, [multiple, value, valueObj]);

  const handleChange = useCallback(
    (value: any) => {
      onChange(value);
      if (!multiple) {
        setOpen(false);
      }
    },
    [multiple, options, onChange],
  );

  const { refs, floatingStyles } = useFloating({
    ...defaultFloatingOptions(position),
    ...floatingOptions,
  });

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const sv = event.target.value.toLowerCase();
      const filtered = initialOptions.filter((opt) => getSearchKey(opt).toLowerCase().includes(sv));
      setOptions(filtered);
      setSearch(event.target.value);

      const idx = filtered.findIndex((opt) => getSearchKey(opt) === selectedLabel);
      setFocusedIndex(idx !== -1 ? idx : 0);
    },
    [initialOptions, selectedLabel],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.keyCode === 32)) {
        handleOpen();
        e.preventDefault();
        e.stopPropagation();
        return;
      } else if (!open) {
        handleOpen();
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev === null || prev === options.length - 1 ? 0 : prev + 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev === 0 || prev === null ? options.length - 1 : prev - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex !== null && !!options?.length) handleChange(options[focusedIndex].value);
          break;
        case 'Escape':
        case 'Tab':
          setOpen(false);
          setFocusedIndex(null);
          break;
      }
    },
    [open, focusedIndex, options],
  );

  useEffect(() => {
    setOptions(initialOptions);
    setSearch(selectedLabel);
  }, [initialOptions, selectedLabel]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      optionRefs.current[focusedIndex || 0]?.scrollIntoView({
        block: 'nearest',
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [open]);

  useEffect(() => {
    if (open && focusedIndex !== null && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex]!.scrollIntoView({
        block: 'nearest',
        behavior: 'auto',
      });
    }
  }, [focusedIndex]);

  const handleOpen = useCallback(() => {
    if (!open) {
      const newIndex = initialOptions.findIndex((option) =>
        typeof searchButtonValue === 'string'
          ? searchButtonValue?.includes(option.label)
          : option.label === searchButtonValue,
      );
      const finalIndex = newIndex !== -1 ? newIndex : 0;

      setFocusedIndex(finalIndex);
      setSearch('');
      setOptions(initialOptions);
    }
    setOpen((state) => !state);
  }, [open, searchButtonValue, initialOptions]);

  return (
    <div
      ref={(element) => {
        refs.setReference(element);
        ref.current = element;
      }}
      className={twMerge('relative font-inter z-1', open && 'z-[2]', className)}
    >
      <Tooltip title={open ? '' : toolTipTitle || ''} middleware={[offset(8), flip(), shift()]}>
        {!withSearch && (
          <button
            disabled={disabled}
            onKeyDown={handleKeyDown}
            type="button"
            className="w-full"
            onClick={(event) => {
              event.preventDefault();
              handleOpen();
            }}
            onBlur={() => {
              setFocusedIndex(null);
            }}
          >
            <Value
              className={classNameValue}
              open={open}
              placeholder={placeholder}
              value={valueObj}
              multiple={multiple}
              isError={isError}
              disabled={disabled}
              icon={icon}
              wrapperClassName={wrapperValueClassName}
            />
          </button>
        )}

        {withSearch && (
          <div
            onKeyDown={handleKeyDown}
            tabIndex={0}
            onClick={(e) => {
              e.preventDefault();
              handleOpen();
            }}
            onBlur={() => {
              // setSearch(selectedLabel);
              setFocusedIndex(null);
            }}
          >
            <TextInput
              className="!cursor-pointer"
              name=""
              prefix={
                icon ||
                (!multiple &&
                  !!(selectedOptions as TOption)?.icon &&
                  ((selectedOptions as TOption).icon as any))
              }
              icon={
                open ? (
                  <Search className="w-5 h-5 text-v2blueGray-550" />
                ) : (
                  <ExpandMore className="w-5 h-5 text-v2blueGray-550" />
                )
              }
              iconPosition="right"
              inputClassName={
                icon || (!multiple && !!(selectedOptions as TOption)?.icon) ? 'pl-2' : undefined
              }
              onChange={handleSearchChange}
              value={open ? search : selectedLabel}
              placeholder={
                selectedLabel
                  ? selectedLabel
                  : typeof placeholder === 'string'
                  ? placeholder
                  : normalizeLabel(placeholder)
              }
              isError={isError}
            />
          </div>
        )}
      </Tooltip>

      <DropdownPopup
        ref={refs.setFloating}
        style={floatingStyles}
        open={open}
        className={twMerge(
          'static w-full gap-[5px] flex flex-col max-h-60 overflow-auto mt-0',
          classNamePopup,
        )}
        position={position}
      >
        {options && options.filter((option) => !option?.hidden).length > 0 ? (
          options.map((option, index) => {
            let selected = false;

            if (option.hidden) return;

            if (multiple && Array.isArray(value)) {
              selected = value.includes(option.value);
            }

            if (!Array.isArray(value) && value !== undefined) {
              selected = option.value === value;
            }

            return (
              <div
                className="hide-cursor"
                ref={(el) => (optionRefs.current[index] = el)}
                key={`${option.value}${index}`}
                onMouseEnter={() => setFocusedIndex(index)}
                onClick={(event) => {
                  event.preventDefault();

                  if (option.disabled) {
                    return;
                  }

                  handleChange(option.value);
                }}
              >
                <Option option={option} selected={selected} preselected={index === focusedIndex} />
              </div>
            );
          })
        ) : (
          <div className="my-[6px] text-v2blueGray-500 text-center">
            <Copy type="copy2">
              <FormattedMessage id="desk-app.no-available-options" />
            </Copy>
          </div>
        )}
      </DropdownPopup>
    </div>
  );
}

export type TOption = {
  label: any;
  value: any;
  disabled?: boolean;
  hidden?: boolean;
  icon?: ReactNode;
  [param: string]: any;
};

export type TDropdownValueVariants = any | any[];
export type IDropdownProps = {
  classNameValue?: string;
  floatingOptions?: UseFloatingOptions;
  onChange?: (value: any) => void;
  value?: TDropdownValueVariants;
  options?: TOption[];
  multiple?: boolean;
  placeholder?: ReactNode;
  Value?: FunctionComponent<ValueComponentProps>;
  Option?: FunctionComponent<OptionComponentProps>;
  className?: string;
  classNamePopup?: string;
  position?: TPopupPositions;
  disabled?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
  open?: boolean;
  isError?: boolean;
  withSearch?: boolean;
  toolTipTitle?: string;
  icon?: ReactNode;
  wrapperValueClassName?: string;
};

const normalizeLabel = (lbl: any): string =>
  typeof lbl === 'string'
    ? lbl
    : lbl?.props?.children
    ? String(lbl.props.children)
    : String(lbl ?? '');

const getSearchKey = (opt: TOption) =>
  typeof opt.searchText === 'string' && opt.searchText
    ? opt.searchText
    : typeof opt.label === 'string'
    ? opt.label
    : normalizeLabel(opt.label);

export default memo<IDropdownProps>(Dropdown);
